import { hideModal, modalBodyType } from '@/redux/navigationSlice';
import { selectModalData } from '@/redux/selectors';
import { Grid, Typography } from '@mui/material';
import { Modal } from '@repo/ui';
import dynamic from 'next/dynamic';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Lazy-load modal body components to reduce initial bundle
const AllDocumentsUploadedModalLazy = dynamic(
  () => import('./allDocumentsUploaded').then((mod) => ({ default: mod.AllDocumentsUploadedModal })),
  { loading: () => null },
);

const CloseSessionBodyLazy = dynamic(
  () => import('./closeSessionBody').then((mod) => ({ default: mod.CloseSessionBody })),
  { loading: () => null },
);

const DeleteFileLazy = dynamic(() => import('./deleteFile').then((mod) => ({ default: mod.DeleteFile })), {
  loading: () => null,
});

const ProspectCompletedBodyLazy = dynamic(
  () => import('./prospectCompleted').then((mod) => ({ default: mod.ProspectCompletedBody })),
  { loading: () => null },
);

const SelectMarketerLazy = dynamic(() => import('./selectMarketer').then((mod) => ({ default: mod.SelectMarketer })), {
  loading: () => null,
});

const UploadFileLazy = dynamic(() => import('./uploadFile').then((mod) => ({ default: mod.UploadFile })), {
  loading: () => null,
});

const ProspectRejectedBodyLazy = dynamic(
  () => import('./prospectRejectedBody').then((mod) => ({ default: mod.ProspectRejectedBody })),
  { loading: () => null },
);

const bodySelector = (body: modalBodyType) => {
  switch (body) {
    case 'uploadFile':
      return <UploadFileLazy />;
    case 'deleteFile':
      return <DeleteFileLazy />;
    case 'closeSession':
      return <CloseSessionBodyLazy />;
    case 'marketerSelection':
      return <SelectMarketerLazy />;
    case 'allDocumentsUploaded':
      return <AllDocumentsUploadedModalLazy />;
    case 'prospectCompleted':
      return <ProspectCompletedBodyLazy />;
    case 'prospectRejected':
      return <ProspectRejectedBodyLazy />;
    default:
      return <></>;
  }
};

/**
 * Renders a Modal component with title and body based on redux.
 *
 * @return {React.JSX.Element} The rendered Modal component.
 */
export const ProspectsModal = () => {
  const modalData = useSelector(selectModalData);
  const dispatch = useDispatch();
  const { open, body, title, onClose } = modalData;
  const BodyComponent = bodySelector(body);

  // Memoized close handler to prevent re-creation on every render
  const handleClose = useCallback(() => {
    dispatch(hideModal());
    onClose?.();
  }, [dispatch, onClose]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Grid
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          maxHeight: { xs: '100%', md: '32rem' },
          py: { xs: 2, md: 4 },
          px: { xs: 0, md: 6 },
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Typography variant="h1">{title}</Typography>
        {BodyComponent}
      </Grid>
    </Modal>
  );
};
